import HelpIcon from "@mui/icons-material/Help";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  Tooltip,
} from "@mui/material";
import MuiPagination from "@mui/material/Pagination";
import {
  DataGridPremium,
  GridCsvExportMenuItem,
  GridExcelExportMenuItem,
  GridPagination,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import _ from "lodash";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import FieldNames from "../../../../defaults/FieldNames.json";
import GridDefinitions from "../../../../defaults/GridDefinitions.json";
import ErrorService from "../../../../services/ErrorService";
import TargetAccomplishmentService from "../../../../services/TargetAccomplishmentService";
import { addCommas, percentFormat } from "../../../../utils/FormatHelper";


function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  const finalWidth = width - 20;
  return {
    finalWidth,
    height
  };
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}


export default function TargetManagementPostFire({
  setColumnVisibilityModel,
  setShowMap,
  currentUserInfo,
  classOptionSelection,
  columnVisibilityModel,
  setGraphRows,
  setLimitedGraphRows,
  fundingDepartmentSelection,
  fundingAgencySelection,
  fundingRegionSelection,
  fundingUnitSelection,
  fundingSubUnitSelection,
  fundingTribeSelection,
  entityTypeSelection,
  fiscalYearSelection
}) {
  const config = useSelector((state) => state.configuration.config);
  const apiRef = useGridApiRef();
  const rootServiceUrl = useSelector(
    (state) => state.configuration.rootServiceUrl
  );
  const viewRootServiceUrl = useSelector(
    (state) => state.configuration.viewRootServiceUrl
  );
  const nifcToken = useSelector((state) => state.configuration.nifcToken);
  const [gridColumns, setGridColumns] = useState([]);
  const [gridRows, setGridRows] = useState([]);
  const [maxCountPerGraph] = useState(25);
  const [isGridLoading, setIsGridLoading] = useState(false);
  const targetManagementGridColDefinitions = [
    GridDefinitions.TargetManagementGrid.name,
    GridDefinitions.TargetManagementGrid.targetAcres,
    GridDefinitions.TargetManagementGrid.actualAcres,
    GridDefinitions.TargetManagementGrid.percent,
  ];

  const [acreCounts, setAcreCounts] = useState({
    AcresCount: 100,
  });

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const highestCount = gridRows ? Math.max(gridRows.length) : 0;
  const totalCount = gridRows ? (gridRows.length) : 0;
  const estPageCount = highestCount > 0 ? Math.ceil(highestCount / paginationModel.pageSize) : 1;
  const rowCountRef = React.useRef(highestCount || 0);
  const rowCount = React.useMemo(() => {
    if (highestCount !== undefined) {
      rowCountRef.current = highestCount;
    }
    return rowCountRef.current;
  }, [highestCount]);
  const currentMaxFrom = ((paginationModel.page) * paginationModel.pageSize);
  const currentMaxTo = ((paginationModel.page+1) * paginationModel.pageSize);
  const fromCount = acreCounts.AcresCount < currentMaxFrom ? acreCounts.AcresCount : currentMaxFrom;
  const toCount = gridRows.length < 25 ? currentMaxFrom + gridRows.length : currentMaxTo;
  const rowHelpText = `The system will display up to 100 items per page. This page is displaying: \n  ${acreCounts.AcresCount} Acre Lines`
  const [isTreatment, setIsTreatment] = useState(true);

  // Formatted budget values
  const [selectionModel, setSelectionModel] = useState({ids: new Set(), type: 'include'});
  const [aggregationModel, setAggregationModel] = useState({
    TotalBudget: "sum",
    RegularBudget: "sum",
    BILBudget: "sum",
    DisasterReliefBudget: "sum",
  });

  useEffect(() => {
    setShowMap(false);
  }, [setShowMap]);

  useEffect(() => {
    establishGridColumns();
  }, [config, rootServiceUrl]);

  //
  // Manage Grid Rows
  //

  useEffect(() => {
    if (
      !config ||
      !viewRootServiceUrl ||
      !fiscalYearSelection ||
      (!fundingDepartmentSelection &&
        !fundingAgencySelection &&
        !fundingRegionSelection &&
        !fundingUnitSelection && 
        !fundingSubUnitSelection &&
        !fundingTribeSelection && 
        !entityTypeSelection
      )
    ) {
      return;
    }

    establishGridColumns();
    setIsGridLoading(true);
    const isCurrentTreatment = classOptionSelection === FieldNames.targetOptions.treatments;
      TargetAccomplishmentService.getAllTargetEntriesPostFire(
        viewRootServiceUrl,
        config,
        fiscalYearSelection,
        fundingDepartmentSelection,
        fundingAgencySelection,
        fundingRegionSelection,
        fundingUnitSelection,
        fundingSubUnitSelection,
        fundingTribeSelection,
        entityTypeSelection,
        isCurrentTreatment
      ).then((resp) => {
        if (resp && resp.length > 0) {
          let estimatedItems = [];
          let actualItems = [];
          if(isCurrentTreatment){
            estimatedItems = resp.filter(x => x[FieldNames.targetFields.class] === FieldNames.classTypes.treatment);
            actualItems = resp.filter(x => x[FieldNames.targetFields.class] === FieldNames.classTypes.actualTreatment);
          } else {
            estimatedItems = resp.filter(x => x[FieldNames.targetFields.class] === FieldNames.classTypes.activity);
            actualItems = resp.filter(x => x[FieldNames.targetFields.class] === FieldNames.classTypes.actualActivity);
          }
          let finalRows = [];

          estimatedItems?.forEach((estItem, index) => {
            const matchingActualItems = actualItems.filter(x => x[FieldNames.targetFields.name]?.toLowerCase()?.trim() === estItem[FieldNames.targetFields.name]?.toLowerCase()?.trim());
            let actualAcres = 0;
            if(matchingActualItems?.length){
              actualAcres = matchingActualItems[0][FieldNames.targetFields.acres];
            }
            const obj = {
              ID: index,
              Name: estItem[FieldNames.targetFields.name],
              EntityType: estItem[FieldNames.targetFields.entityType],
              TargetAcres: estItem[FieldNames.targetFields.acres],
              ActualAcres: actualAcres,
              Percent: (estItem[FieldNames.targetFields.acres] && actualAcres) ? (Math.round((actualAcres / estItem[FieldNames.targetFields.acres]) * 100)) : 0
            }
            finalRows.push(obj);
          })

          setGridRows(finalRows);
          const sortedFinalRows = _.cloneDeep(finalRows);
          let tempSorted = sortedFinalRows.sort((a, b) => a[FieldNames.targetFields.constructedTargetAcres] > b[FieldNames.targetFields.constructedTargetAcres] ? -1 : 1);
          setGraphRows(tempSorted)
          setLimitedGraphRows(tempSorted.slice(0, maxCountPerGraph))
        } else {
          setGridRows([]);
          setGraphRows([]);
          setLimitedGraphRows([]);
        }
        setIsGridLoading(false);
      }).catch(error => {
        setIsGridLoading(false);
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
  }, [
    config,
    rootServiceUrl,
    viewRootServiceUrl,
    fiscalYearSelection,
    fundingDepartmentSelection,
    fundingAgencySelection,
    fundingRegionSelection,
    fundingUnitSelection,
    fundingTribeSelection,
    fundingSubUnitSelection,
    currentUserInfo,
    isTreatment,
    nifcToken,
    entityTypeSelection,
    classOptionSelection
  ]);

  const establishGridColumns = () => {
    let columns = [];
    targetManagementGridColDefinitions.forEach((colDef) => {
      columns.push({
        field: colDef.value,
        headerName: colDef.header,
        description: colDef.description,
        type: colDef.type,
        hideable: colDef.hideable,
        sortable: colDef.sortable,
        editable: colDef.editable,
        groupable: false,
        flex: colDef.flex,
        align: colDef.align,
        disableExport: colDef.disableExport,
        aggregable: (colDef.value === "TargetAcres" || colDef.value === "ActualAcres"),
        valueFormatter: (value) => {
          if(colDef.value === "TargetAcres" || colDef.value === "ActualAcres"){
            return addCommas(value);
          } else if(colDef.value === "Percent"){
            if(value === null || value === undefined) {
              return null;
            } else {
              return `${value}%`;
            }
          } else {
            return value;
          }
        },
      });
    });

    setGridColumns(columns);
  };

  function Pagination({ page, onPageChange, className }) {
    const pageCount = estPageCount;
    return (
      <>
        {isGridLoading ? (
          <Box sx={{ paddingRight: "15px !important", paddingLeft: "15px !important", paddingTop: "5px !important", paddingBottom: "5px !important" }}>
            <CircularProgress size="1.5rem"></CircularProgress>
          </Box>
        ) : (
          <MuiPagination
            color="primary"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
              onPageChange(event, newPage - 1);
            }}
          />
        )}
      </>
    );
  }

  function CustomPagination(props) {
    if(acreCounts.AcresCount > 0)
    {    
        return (
          <Stack direction="row" spacing={0}>
            <Tooltip 
              title={
                <div style={{ whiteSpace: 'pre-line' }}>{rowHelpText}</div>
              } 
              enterDelay={100}
            >
            <HelpIcon fontSize="small" color="primary" style={{ marginTop: "15px" }}/>
            </Tooltip>
            <GridPagination 
              ActionsComponent={Pagination}
              labelDisplayedRows= {() => customLabelDisplayedRows(fromCount,toCount,totalCount)}
              {...props} 
            />
          </Stack>
        )
    }
    else
    { return null; }
  }

  function customLabelDisplayedRows( from, to, count ) { 
    return `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`; 
  }

  

  //
  // General Page Methods
  //


  // --- Grid Logic ---
  function CustomTargetGridToolbar(props) {
    return (
      <GridToolbarContainer>
        <GridToolbarDensitySelector />
        <CustomExportContainer />
      </GridToolbarContainer>
    );
  }

  function CustomExportContainer(props) {
    return (
      <GridToolbarExportContainer {...props}>
        <GridCsvExportMenuItem
          options={exportOptions}
          onClick={() => apiRef.current.exportDataAsCsv(exportOptions)}
        />
        <GridExcelExportMenuItem
          options={exportOptions}
          onClick={() => apiRef.current.exportDataAsExcel(exportOptions)}
        />
      </GridToolbarExportContainer>
    );
  }

  const exportOptions = {
    getRowsToExport: () => gridFilteredSortedRowIdsSelector(apiRef),
  };


  return (
    <DataGridPremium
      apiRef={apiRef}
      style={{ borderTop: 'none', height: 500}}
      getRowId={(row) => row['ID']}
      rows={gridRows}
      columns={gridColumns}
      rowsPerPageOptions={[10]}
      rowSelectionModel={selectionModel}
      rowCount={rowCount}
      pagination
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[25]}
      showToolbar
      slots={{
        toolbar: CustomTargetGridToolbar,
        loadingOverlay: LinearProgress,
        pagination: CustomPagination,
      }}
      loading={isGridLoading}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) =>
        setColumnVisibilityModel(newModel)
      }
      experimentalFeatures={{ aggregation: true }}
      aggregationModel={aggregationModel}
      onAggregationModelChange={(newModel) => {
        setAggregationModel(newModel);
      }}
      sx={{
        "& .MuiDataGrid-aggregationColumnHeaderLabel": {
          lineHeight: "0.25rem",
        },
      }}
    />
  );
}
